<template>
  <section class="content-resource-preview" :class="resourceClass">
    <div v-if="!resource.hasResource" class="content-resource-preview__empty">
      暂无可展示的文件内容
    </div>
    <template v-else>
      <div class="content-resource-preview__canvas">
        <BaseImage
          v-if="previewType === FILE_TYPE.IMAGE"
          :src="resourceUrl"
          :alt="resource.fileName || '图片预览'"
          class="content-resource-preview__image"
          preview
          preload
          :progressive="false"
          disable-oss-format
          disable-oss-optimization
          @error="handlePreviewError"
        />
        <video
          v-else-if="previewType === FILE_TYPE.VIDEO"
          ref="videoRef"
          :src="resourceUrl"
          :poster="posterUrl || undefined"
          class="content-resource-preview__video"
          controls
          controlslist="nodownload"
          playsinline
          preload="metadata"
          @contextmenu.prevent
          @error="handlePreviewError"
        ></video>
        <audio
          v-else-if="previewType === FILE_TYPE.AUDIO"
          :src="resourceUrl"
          class="content-resource-preview__audio"
          controls
          preload="metadata"
          @error="handlePreviewError"
        ></audio>
        <section v-else-if="previewType === FILE_TYPE.TXT" class="content-resource-preview__text">
          <div v-if="textLoading" class="content-resource-preview__state">文件加载中…</div>
          <div v-else-if="textError" class="content-resource-preview__state">{{ textError }}</div>
          <pre v-else class="content-resource-preview__text-content">{{ textContent }}</pre>
        </section>
        <iframe
          v-else-if="previewType === FILE_TYPE.PDF"
          :src="previewUrl"
          class="content-resource-preview__iframe"
          frameborder="0"
          :title="`${resource.fileName || 'PDF 文件'}预览`"
          @error="handlePreviewError"
        ></iframe>
        <iframe
          v-else-if="previewType === FILE_PREVIEW_OFFICE"
          :src="previewUrl"
          class="content-resource-preview__iframe"
          frameborder="0"
          :title="`${resource.fileName || 'Office 文件'}预览`"
          @load="handleOfficeLoad"
          @error="handlePreviewError"
        ></iframe>
        <div v-else class="content-resource-preview__unsupported">
          <BaseImage
            :src="getFileIconPath(resource)"
            :alt="resource.fileName || '文件'"
            class="content-resource-preview__file-icon"
          />
          <p class="content-resource-preview__unsupported-title">暂不支持在线预览该文件</p>
          <p class="content-resource-preview__unsupported-name">
            {{ resource.fileName || '未命名文件' }}
          </p>
        </div>
      </div>

      <div
        v-if="previewError || officeLoadingSlow"
        class="content-resource-preview__notice"
        role="status"
      >
        {{
          previewError
            ? '预览加载失败，请使用下方操作查看文件。'
            : 'Office 文件加载较慢，可继续等待或使用下方操作查看。'
        }}
      </div>

      <div class="content-resource-preview__actions">
        <template v-if="needsManualRefresh">
          <span class="content-resource-preview__notice">预览失效时，请刷新预览。</span>
          <a-button @click="emit('refresh-required')">刷新预览</a-button>
        </template>
        <a
          v-if="isOfficePreview"
          :href="previewUrl"
          class="content-resource-preview__open-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          新窗口打开
        </a>
        <a-button
          v-if="showDownloadAction && canDownload"
          type="primary"
          :loading="downloading"
          @click="emit('download', resource)"
        >
          <template #icon><IconDownload /></template>
          下载文件
        </a-button>
      </div>
    </template>
  </section>
</template>

<script setup>
/**
 * @description 官网服务详情的主资源预览组件。
 * @param {Object} resource - 已解析的详情主资源
 * @param {string} resource.url - 文件地址
 * @param {string} resource.fileName - 文件名称
 * @param {string} resource.fileType - 文件类型
 * @param {string} resource.previewType - 预览类型
 * @param {boolean} resource.hasResource - 是否存在文件资源
 * @param {boolean} downloading - 是否正在下载
 * @param {boolean} showDownloadAction - 是否显示预览区底部下载按钮
 * @emits download - 请求下载当前资源
 */

import { computed, onUnmounted, ref, watch } from 'vue'
import { IconDownload } from '@arco-design/web-vue/es/icon'
import { getOssOriginUrl } from '@/utils'
import {
  createContentResourceRefreshGate,
  getContentResourceRefreshDelay,
} from '@/utils/contentResource'
import { FILE_TYPE } from '@/utils/constant'
import { getOssSignedUrlExpiresAt, isOssSignedUrl } from '@/utils/ossSignedUrl'
import {
  FILE_PREVIEW_OFFICE,
  canDownloadFile,
  getFileIconPath,
  getFilePreviewUrl,
} from '@/utils/download'
import { useVideoPlayer } from '~/composables/useVideoPlayer'

const props = defineProps({
  resource: {
    type: Object,
    required: true,
  },
  downloading: {
    type: Boolean,
    default: false,
  },
  showDownloadAction: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['download', 'refresh-required'])
const videoRef = ref(null)
const previewError = ref(false)
const textContent = ref('')
const textError = ref('')
const textLoading = ref(false)
const officeLoadingSlow = ref(false)
const { syncVideoBindings } = useVideoPlayer()
let textRequestId = 0
let officeLoadTimer = null
let signedResourceRefreshTimer = null
const refreshGate = createContentResourceRefreshGate()
const previewErrorRefreshGate = createContentResourceRefreshGate()

const previewType = computed(() => props.resource?.previewType || '')
const resourceUrl = computed(() => getOssOriginUrl(props.resource?.url || ''))
const posterUrl = computed(() => getOssOriginUrl(props.resource?.poster || ''))
const previewUrl = computed(() =>
  getFilePreviewUrl({
    url: resourceUrl.value,
    fileName: props.resource?.fileName,
    fileType: props.resource?.fileType,
  })
)
const resourceIdentity = computed(() => resourceUrl.value.replace(/[?#].*$/, ''))
const isOfficePreview = computed(() => previewType.value === FILE_PREVIEW_OFFICE)
const needsManualRefresh = computed(
  () =>
    (previewType.value === FILE_TYPE.PDF || isOfficePreview.value) &&
    isOssSignedUrl(resourceUrl.value) &&
    getOssSignedUrlExpiresAt(resourceUrl.value) === null
)
const canDownload = computed(() => {
  return previewType.value !== FILE_TYPE.VIDEO && canDownloadFile(props.resource)
})
const resourceClass = computed(() => ({
  'content-resource-preview--video-error':
    previewType.value === FILE_TYPE.VIDEO && previewError.value,
  'content-resource-preview--office': isOfficePreview.value,
  'content-resource-preview--unsupported': !props.resource?.isPreviewable,
}))

/**
 * @description 清理 Office Viewer 的慢加载提示计时器。
 * @returns {void}
 */
const clearOfficeLoadTimer = () => {
  if (!officeLoadTimer) return
  clearTimeout(officeLoadTimer)
  officeLoadTimer = null
}

/**
 * @description 清理签名资源到期前刷新计时器，避免切换内容后刷新旧资源。
 * @returns {void}
 */
const clearSignedResourceRefreshTimer = () => {
  if (!signedResourceRefreshTimer) return
  clearTimeout(signedResourceRefreshTimer)
  signedResourceRefreshTimer = null
}

/**
 * @description 在私有 OSS 签名到期前刷新详情，避免跨域 iframe 的 403 无法可靠触发 error 事件。
 * @param {string} url - 当前资源访问地址
 * @returns {void}
 */
const scheduleSignedResourceRefresh = (url) => {
  clearSignedResourceRefreshTimer()
  const usesCrossOriginIframe =
    previewType.value === FILE_TYPE.PDF || previewType.value === FILE_PREVIEW_OFFICE
  if (!import.meta.client || !usesCrossOriginIframe) return

  const delay = getContentResourceRefreshDelay(url)
  if (delay === null) return

  signedResourceRefreshTimer = setTimeout(() => {
    signedResourceRefreshTimer = null
    if (resourceUrl.value !== url || !refreshGate.shouldRequest(url)) return
    emit('refresh-required', { reason: 'signed-expiring', url })
  }, delay)
}

/**
 * @description 记录资源预览加载失败状态。
 * @returns {void}
 */
const handlePreviewError = () => {
  previewError.value = true
  clearOfficeLoadTimer()
  if (previewErrorRefreshGate.shouldRequest(resourceIdentity.value)) {
    emit('refresh-required', { reason: 'preview-error', url: resourceUrl.value })
  }
}

/**
 * @description 标记 Office Viewer 已完成首屏加载。
 * @returns {void}
 */
const handleOfficeLoad = () => {
  officeLoadingSlow.value = false
  clearOfficeLoadTimer()
}

watch(videoRef, (element) => {
  syncVideoBindings(element ? [element] : [])
})

watch(
  [resourceUrl, previewType],
  async ([url, type]) => {
    const requestId = ++textRequestId
    previewError.value = false
    textContent.value = ''
    textError.value = ''
    textLoading.value = false
    officeLoadingSlow.value = false
    clearOfficeLoadTimer()
    scheduleSignedResourceRefresh(url)

    if (type === FILE_PREVIEW_OFFICE && url && import.meta.client) {
      officeLoadTimer = setTimeout(() => {
        officeLoadingSlow.value = true
      }, 5000)
    }

    if (!url || type !== FILE_TYPE.TXT || !import.meta.client) return

    textLoading.value = true
    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const content = await response.text()
      if (requestId !== textRequestId) return
      textContent.value = content
    } catch (error) {
      if (requestId !== textRequestId) return
      textError.value = '文本文件加载失败，请下载后查看。'
      handlePreviewError()
    } finally {
      if (requestId === textRequestId) textLoading.value = false
    }
  },
  { immediate: true }
)

watch(
  () => props.resource?.contentId,
  () => {
    refreshGate.reset()
    previewErrorRefreshGate.reset()
  }
)

onUnmounted(() => {
  textRequestId++
  clearOfficeLoadTimer()
  clearSignedResourceRefreshTimer()
  syncVideoBindings([])
})
</script>

<style lang="scss" scoped>
.content-resource-preview {
  width: 100%;
  overflow: hidden;
  border: 1px solid $border-light-color;
  border-radius: $radius-6;
  background-color: $bg-white;

  &__canvas {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 360px;
    overflow: hidden;
    background-color: $bg-soft-color;
  }

  &__iframe {
    display: block;
    width: 100%;
    min-height: 640px;
    border: 0;
    background-color: $bg-white;
  }

  &__image {
    width: 100%;
    min-height: 360px;
    max-height: 720px;

    :deep(.base-image__optimized),
    :deep(.base-image__inner),
    :deep(.arco-image),
    :deep(.arco-image-img),
    :deep(img) {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__video {
    display: block;
    width: 100%;
    max-height: 720px;
    background-color: $bg-dark-light;
    object-fit: contain;
  }

  &--video-error {
    .content-resource-preview__canvas {
      min-height: 0;
      aspect-ratio: 16 / 9;
      background-color: $bg-dark-light;
    }

    .content-resource-preview__video {
      height: 100%;
      max-height: none;
      aspect-ratio: 16 / 9;
    }
  }

  &__audio {
    width: min(720px, calc(100% - 48px));
  }

  &__text {
    width: 100%;
    min-height: 360px;
    max-height: 720px;
    overflow: auto;
    background-color: $bg-white;
  }

  &__text-content {
    min-height: 100%;
    margin: 0;
    padding: 24px;
    box-sizing: border-box;
    color: $text-content-color;
    font-size: 14px;
    line-height: 22px;
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__state,
  &__empty,
  &__unsupported {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 24px;
    box-sizing: border-box;
    color: $text-subtitle-color;
    font-size: 14px;
    line-height: 22px;
    text-align: center;
  }

  &__empty {
    min-height: 160px;
  }

  &__file-icon {
    width: 56px;
    height: 56px;
    margin-bottom: 12px;
  }

  &__unsupported-title,
  &__unsupported-name {
    margin: 0;
  }

  &__unsupported-title {
    color: $text-title-color;
    font-weight: 500;
  }

  &__unsupported-name {
    max-width: 100%;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__notice {
    padding: 12px 16px 0;
    color: $text-subtitle-color;
    font-size: 14px;
    line-height: 22px;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px;
    background-color: $bg-white;
  }

  &__open-link {
    color: $primary-color;
    font-size: 14px;
    line-height: 32px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @include respond-to-max('lg') {
    border-radius: $radius-md;

    &__canvas,
    &__image,
    &__text {
      min-height: 240px;
    }

    &__iframe {
      min-height: 440px;
    }

    &__video {
      max-height: 480px;
    }

    &__text-content {
      padding: 16px;
      font-size: 13px;
      line-height: 20px;
    }

    &__actions {
      padding: 12px;
    }
  }
}
</style>
